import React from 'react';
import {
  Alert,
  Box,
  Container,
  Grid,
  Stack,
  Typography,
  Link as MuiLink,
  AlertTitle,
  Skeleton,
  Button,
  Tooltip,
  useMediaQuery,
  Theme,
} from '@mui/material';
import { Partner, TripRequestStatus } from '@marageti/z4-sdk/lib/travel';
import { UserStatus } from '@marageti/z4-sdk/lib/auth';
import { ZAvatar } from '@marageti/z4-lib';
import {
  ViewProps,
  TitleProps,
  InviteCoTravelersSectionProps,
} from './types';
import {
  DetailedMatchCard,
  ZicassoCareSpecialistCard,
  TripHeroImage,
} from '../../components/trips';
import BookingDetails from '../../components/booking-details';
import TripMatchDetails from '../../components/trip-match-details';
import TripRequestStatusChip from '../../components/chips/trip-request';
import { TripType } from '../../components/booking-details/types';
import CoTravelerView from './cotraveler';
import InviteCoTravelerView from './invite';

const TripTitle = ({
  titleCountry,
  tripRequest,
}: TitleProps) => (
  <Box>
    <Typography sx={{ marginBottom: 0.75 }} variant="subtitle1" color="text.secondary">Trip To</Typography>
    <Typography
      data-e2e="ztest-trip-to"
      variant="h2"
      sx={{
        marginBottom: 2,
      }}
    >
      {titleCountry || <Skeleton width={200} />}
    </Typography>

    <TripRequestStatusChip tripRequest={tripRequest} />
  </Box>
);

const TripMatches = ({
  activeMatches,
  travelerId,
  partners,
  isCanceled,
}:
{
  activeMatches: any[],
  travelerId: string,
  partners: Partner[] | undefined,
  isCanceled: boolean,
}) => {
  const showDefaultMessage = !isCanceled && !activeMatches?.length;
  return (
    <Box>
      <Stack spacing={showDefaultMessage ? 1 : 2}>
        {showDefaultMessage && (
          <>
            <Typography variant="bodyBold" color="primary">
              We&apos;re Working on Your Request
            </Typography>
            <Typography sx={{ color: '#4A5152' }}>
              Here at Zicasso, we take the time to personally review all requests. We will reach out to you as soon as we complete our review.
            </Typography>
          </>
        )}

        {(activeMatches.length > 0) && (
          <Typography variant="bodyBold" color="primary">
            Travel Partners Connected to You
          </Typography>
        )}

        {activeMatches.map((match: any) => (
          <DetailedMatchCard
            key={match.id}
            tripMatch={match}
            travelerId={travelerId}
            partners={partners}
          />
        ))}

      </Stack>
    </Box>
  );
};

const InviteCoTravelersSection = ({
  tripRequest,
  toggleInvite,
  openCoTravelerCard,
}: InviteCoTravelersSectionProps) => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column-reverse',
        alignItems: 'flex-start',
        gap: 2,
        width: '100%',
      }}
    >
      <Box
        sx={{
          flex: '1 0 100%',
        }}
      >
        <Stack gap={1}>
          <Typography variant="bodyBold" color="primary">
            Invite Co-Travelers to Join This Trip
          </Typography>

          <Typography variant="body1" color="text.secondary" mr={3}>
            Invite your family and friends to help plan your trip!
            They&apos;ll get access to the trip details and documents, and can join the conversation with the travel specialists curating your trip.
          </Typography>

          <Button
            variant="outlined"
            sx={{ width: { xs: '100%', md: 'fit-content' }, borderColor: (theme) => theme.palette.dividerLight, marginTop: 1 }}
            onClick={toggleInvite}
          >
            <Typography variant={isMobile ? 'captionBold' : 'bodyBold'} color="primary">
              Send Invite
            </Typography>
          </Button>
        </Stack>
      </Box>
      {tripRequest.travelers.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            mb: 0,
            mt: 0,
            flex: '1 0 100%',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
            {tripRequest.travelers.map((traveler, index) => {
              const isPending = traveler.userInfo?.[0]?.status === UserStatus.PENDING;
              const tooltipText = isPending
                ? `${traveler.givenName} ${traveler.familyName} (Pending Confirmation)`
                : `${traveler.givenName} ${traveler.familyName}`;
              return (
                <Tooltip title={tooltipText} placement="top">
                  <span>
                    <ZAvatar
                      size={40}
                      src={traveler.avatar}
                      name={`${traveler.givenName} ${traveler.familyName}`}
                      onClick={() => openCoTravelerCard(traveler.id)}
                      key={traveler.id}
                      sx={{
                        opacity: isPending ? 0.4 : 1,
                        '&:hover': { cursor: 'pointer' },
                        boxSizing: 'content-box',
                        border: '2px solid #FFFFFF',
                        marginLeft: index > 0 ? '-8px' : 0,
                        zIndex: tripRequest.travelers.length - index,
                      }}
                    />
                  </span>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

const TripDetailsView = ({
  activeMatches,
  partners,
  canInviteCoTravelers,
  cooperative,
  photoId,
  isLoading,
  salesRecordings,
  soldMatches,
  titleCountry,
  travelerId,
  tripRequest,
  toggleCancel,
  isInviting,
  toggleInvite,
  openCoTravelerCard,
  viewingCoTravelerCardId,
  mutate,
}: ViewProps) => (
  <>
    <Container maxWidth="xl">
      <Box sx={{ pt: 0, pb: 6 }}>
        <Grid
          container
          spacing={{
            desktop: 6,
            mobile: 4,
          }}
        >
          <Grid item xs={12} sm={12} md={6}>
            <TripHeroImage
              photoId={photoId}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <Stack
              spacing={4}
              sx={{
                flex: { desktop: '1 1 auto' },
                maxWidth: {
                  desktop: '642px',
                },
                mb: 6,
              }}
            >
              <TripTitle
                titleCountry={titleCountry}
                tripRequest={tripRequest}
              />
              {/* Hide all booking details if tripRequest.visibilityFlags.hideDetails is true  */}
              {tripRequest.status !== TripRequestStatus.SoldStatus
                  && soldMatches.length === 0
                  && !tripRequest.visibilityFlags.hideDetails
                  && (
                    <BookingDetails
                      tripType={TripType.TripRequest}
                      data-e2e="ztest-trip-original-request"
                      tripBudget={tripRequest.totalBudget}
                      departureDate={tripRequest.departureDate}
                      description={tripRequest.tripDescription}
                      destinations={tripRequest.destinations}
                      isLoading={isLoading}
                      numAdults={tripRequest.numberOfAdults}
                      numChildren={tripRequest.numberOfChildren}
                      returnDate={tripRequest.returnDate}
                      salesRecordings={salesRecordings?.[tripRequest.id]}
                      hideDetails={false}
                    />
                  )}

              {/* This should display booking details for multiple trips if it is coop */}
              {soldMatches.map((tripMatch: any) => (
                <TripMatchDetails
                  isLoading={isLoading}
                  key={tripMatch.id}
                  salesRecordings={salesRecordings?.[tripMatch.id]}
                  tripMatch={tripMatch}
                  tripRequest={tripRequest}
                />
              ))}

              {cooperative && (
              <Alert severity="info" sx={{ marginBottom: 2 }}>
                <AlertTitle>Cooperative Match</AlertTitle>
                This is a cooperative match, where you will book with 1 partner for different legs of your trip.
              </Alert>
              )}

              <ZicassoCareSpecialistCard
                travelerId={travelerId}
                tripRequestId={tripRequest.id}
                conversationId={tripRequest.travelerToZicassoId}
              />

              <TripMatches
                travelerId={travelerId}
                activeMatches={activeMatches}
                partners={partners}
                isCanceled={tripRequest.status === TripRequestStatus.CanceledStatus}
              />

              {canInviteCoTravelers && tripRequest.status !== TripRequestStatus.CanceledStatus && (
              <InviteCoTravelersSection
                tripRequest={tripRequest}
                toggleInvite={toggleInvite}
                openCoTravelerCard={openCoTravelerCard}
              />
              )}

              {(tripRequest.status === TripRequestStatus.VerifiedStatus
                  || tripRequest.status === TripRequestStatus.PendingStatus) && (tripRequest.travelers[0].id === travelerId) && (
                    <MuiLink
                      component="button"
                      variant="caption"
                      onClick={toggleCancel}
                      sx={{
                        cursor: 'pointer',
                        width: 'fit-content',
                      }}
                    >
                      I want to cancel this Trip
                    </MuiLink>
              )}

            </Stack>
          </Grid>
        </Grid>
      </Box>
    </Container>
    {isInviting && (
      <InviteCoTravelerView
        onClose={toggleInvite}
        tripRequest={tripRequest}
        onSuccess={mutate}
      />
    )}
    {viewingCoTravelerCardId && (
      <CoTravelerView
        onClose={() => openCoTravelerCard(null)}
        tripRequest={tripRequest}
        coTravelerId={viewingCoTravelerCardId}
        onSuccess={mutate}
      />
    )}
  </>
);

export default TripDetailsView;
